import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import Modal from "react-bootstrap/Modal";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  SELL,
  USER_TYPE_AGENT,
  PROPERTY_TYPE_TO_CATEGORY,
  INVENTORY_SELL,
} from "@/constants/common";
import { getUserId, getUserType } from "@/helpers/common";
import { handleMapImageUpload } from "@/helpers/inventory";
import {
  getPropertyCategoriesWithTypes,
  getNiceToHaves,
} from "@/services/commonServices";
import { saveMemberInventoryData } from "@/services/inventoryService";
import {
  CITY_NOT_A_PROPERTY_MESSAGE,
  describeAddress,
  geocodeAddress,
  isStreetAddress,
  lookupPropertyFacts,
  matchFeaturesToNiceToHaves,
} from "@/helpers/quickAddProperty";
import { useInventoryLocationRestrictions } from "@/hooks/useInventoryLocationRestrictions";
import "./QuickAddPropertyModal.scss";

const SOURCE_LABELS = {
  mls: "Prefilled from the MLS record. Correct anything that looks off.",
  public:
    "Prefilled from public property data. Correct anything that looks off.",
};

// Money reads as a number, not a price, without a symbol and separators.
const formatMoney = (value) => {
  const n = Number(String(value ?? "").replace(/[^\d.]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return "";
  return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

const numberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(String(value).replace(/[^\d.]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const EMPTY_VALUES = {
  prprtyType: "",
  noBedRm: "",
  noBathRm: "",
  sqFt: "",
  lotSize: "",
  yearBuilt: "",
  sellAskngPrice: "",
  grgeSpots: "",
  stories: "",
};

const QuickAddPropertyModal = (props) => {
  const { show, address, onHide, onCreated, selectedClient } = props;

  const { isRestrictedState, restrictionMessage } =
    useInventoryLocationRestrictions();

  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [addressParts, setAddressParts] = useState(null);
  const [source, setSource] = useState(null);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [features, setFeatures] = useState([]);
  const [niceToHaves, setNiceToHaves] = useState([]);
  const [enriching, setEnriching] = useState(false);
  const [featureCatalogue, setFeatureCatalogue] = useState([]);
  const [values, setValues] = useState(EMPTY_VALUES);
  // Fields the agent typed into. Not the same as "non-empty" - the lookups fill
  // fields too, and reading those as the agent's is what stopped the MLS
  // correcting a public guess that arrived first.
  const editedRef = useRef(new Set());

  // The full amenity list, so a missed feature can be added rather than only
  // the auto-detected ones being displayed back.
  useEffect(() => {
    getNiceToHaves(INVENTORY_SELL)
      .then((response) => {
        const result = _.get(response, "result", null);
        if (!result) return;
        const flat = [];
        Object.entries(result).forEach(([tab, options]) => {
          (options || []).forEach((o) =>
            flat.push({ value: o.Id, label: o.Text, id: o.Id, tab }),
          );
        });
        setFeatureCatalogue(flat);
      })
      .catch((err) => console.log("Error", err));
  }, []);

  useEffect(() => {
    getPropertyCategoriesWithTypes("buy")
      .then((response) => {
        if (response?.errorCode !== 0) return;
        const categories = _.get(response, "result", []) || [];
        setPropertyTypes(categories.flatMap((c) => c.types || []));
      })
      .catch((err) => console.log("Error", err));
  }, []);

  useEffect(() => {
    if (!show || !address) return;
    let cancelled = false;

    const resolve = async () => {
      setStatus("loading");
      setError("");
      setSource(null);
      // Everything below is about THIS address. Without clearing it, the second
      // property an agent adds inherits the first one's record: the fill only
      // writes into fields it finds empty - so it can never overwrite something
      // it typed itself - and the previous beds, baths, price and features all
      // survive intact under a new address.
      setValues(EMPTY_VALUES);
      editedRef.current = new Set();
      setFeatures([]);
      setNiceToHaves([]);
      setAddressParts(null);
      // Cleared here rather than only on success, so an address that fails to
      // geocode cannot leave the previous run's fields shimmering forever.
      setEnriching(false);

      let geocoded = null;
      try {
        geocoded = await geocodeAddress(address);
      } catch (err) {
        console.log("Quick add: geocoding failed", err);
      }
      if (cancelled) return;

      if (!geocoded) {
        setStatus("error");
        setError("We couldn't find that address. Check it and try again.");
        return;
      }
      if (!isStreetAddress(geocoded)) {
        setStatus("error");
        setError(CITY_NOT_A_PROPERTY_MESSAGE);
        return;
      }

      const parts = describeAddress(geocoded);

      if (isRestrictedState(parts?.state)) {
        setStatus("error");
        setError(restrictionMessage(parts.state));
        return;
      }

      setAddressParts(parts);

      // The property lookups hit external providers and can take 15s+ on an
      // address they have no record of, so the form is shown as soon as the
      // address resolves and fills in underneath them. Called on every partial
      // as well as at the end, so whichever provider answers first fills the
      // form rather than both waiting on the slower one.
      const applyFacts = (facts) => {
        if (cancelled) return;
        setSource(facts.source);
        setFeatures(facts.features || []);
        // facts.values already holds the precedence decision, so applying the
        // whole set each time lets a late MLS answer correct an early public
        // one. Only what the agent typed is off limits.
        setValues((prev) => {
          const next = { ...prev };
          Object.entries(facts.values || {}).forEach(([k, v]) => {
            if (editedRef.current.has(k)) return;
            if (v === null || v === undefined || v === "") return;
            next[k] = v;
          });
          return next;
        });
      };

      const enrich = lookupPropertyFacts(parts, geocoded, applyFacts).then(
        (facts) => {
          if (cancelled) return;
          applyFacts(facts);
          setEnriching(false);
          matchFeaturesToNiceToHaves(facts.features).then((matched) => {
            if (!cancelled) setNiceToHaves(matched);
          });
        },
      );

      setEnriching(true);
      setStatus("ready");
      await enrich;
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [show, address]);

  const setValue = (key) => (e) => {
    editedRef.current.add(key);
    setValues((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // Pending means "still waiting on this one", not "still waiting on anything".
  // A field the fast provider already answered must stop shimmering at once,
  // or a form that is half filled still reads as stuck.
  const pendingClass = (key) => {
    const v = values[key];
    return enriching && (v === "" || v === null || v === undefined)
      ? " is-pending"
      : "";
  };

  // The only field the inventory table refuses to take as null. Everything else
  // stays optional so a thin lookup does not turn quick add back into a form.
  const missingPropertyType = !values.prprtyType;

  const handleConfirm = async () => {
    if (!addressParts || saving || missingPropertyType) return;
    setSaving(true);

    // The map thumbnail is cosmetic and depends on S3 - never let it stop the
    // property being created.
    let mapImg = "";
    try {
      mapImg = await handleMapImageUpload({
        lat: addressParts.lat,
        lng: addressParts.lng,
        mapZoom: null,
        s3ImageFolder: "",
      });
    } catch (err) {
      console.log("Quick add: map image failed", err);
    }

    const propertyTypeId = numberOrNull(values.prprtyType);
    const payload = {
      locAddress: addressParts.locAddress,
      street: addressParts.street,
      city: addressParts.city,
      county: addressParts.county,
      state: addressParts.state,
      zip: addressParts.zip,
      lat: addressParts.lat,
      lng: addressParts.lng,
      mapImg: mapImg || "",
      prprtyType: propertyTypeId,
      propertyCategory: propertyTypeId
        ? PROPERTY_TYPE_TO_CATEGORY[propertyTypeId] || null
        : null,
      noBedRm: numberOrNull(values.noBedRm),
      noBathRm: numberOrNull(values.noBathRm),
      sqFt: numberOrNull(values.sqFt),
      lotSize: numberOrNull(values.lotSize),
      yearBuilt: values.yearBuilt ? String(values.yearBuilt) : "",
      grgeSpots: numberOrNull(values.grgeSpots),
      stories: numberOrNull(values.stories),
      sellAskngPrice: numberOrNull(values.sellAskngPrice),
      nice2Have: niceToHaves.map((f, i) => ({ ...f, order: i + 1 })),
      proprtyPurpose: [SELL],
      isConfirmOwner: true,
      isConfirmPropLoc: true,
      clientId: selectedClient?.value || null,
      isAgentAsSeller: !selectedClient?.value,
      isAgentCreated: getUserType() === USER_TYPE_AGENT,
      userId: getUserId(),
      cmpldPerctg: 100,
      actvStep: 9,
      IsSavedDraft: false,
      IsPublished: true,
    };

    try {
      const response = await saveMemberInventoryData(payload);
      const result = _.get(response, "result", null);
      if (response?.errorCode === 0 && result?.inventoryId) {
        toast.dismiss();
        toast.success("Property added", { autoClose: 1500 });
        onCreated?.({
          Inv_Id: result.inventoryId,
          Location: payload.locAddress,
          Street: payload.street,
          City: payload.city,
          State: payload.state,
          ZIP: payload.zip,
          AskingPrice_Sell: payload.sellAskngPrice,
        });
      } else {
        // "Try again" on its own was misleading: a rejected record fails
        // identically every time, so point at the details instead. The response
        // is logged rather than shown - it carries SQL.
        console.log("Quick add: save rejected", response);
        toast.dismiss();
        toast.error("We couldn't add that property. Check the details above.");
      }
    } catch (err) {
      console.log("Quick add: save failed", err);
      toast.dismiss();
      toast.error("We couldn't reach the server. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="quick-add-modal"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>Confirm the property</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {status === "loading" && (
          <div className="quick-add-modal__loading">
            <div className="quick-add-modal__loading-head">
              <span className="quick-add-modal__spinner" aria-hidden="true" />
              <p>Pulling the property record...</p>
            </div>
            <ul className="quick-add-modal__value">
              <li>See how many buyers are already looking for a home like this</li>
              <li>Get alerted the moment a matching Want-Listing is posted</li>
              <li>Pull a demand report to take to the listing appointment</li>
            </ul>
            <div className="quick-add-modal__skeleton" aria-hidden="true">
              {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                <span key={i} />
              ))}
            </div>
            <p className="quick-add-modal__value-note">
              Hidden to you until you choose to share it.
            </p>
          </div>
        )}

        {status === "error" && (
          <p className="quick-add-modal__error">{error}</p>
        )}

        {status === "ready" && (
          <>
            <div className="quick-add-modal__subject">
              <h4 className="quick-add-modal__address">
                {addressParts?.street || addressParts?.locAddress}
              </h4>
              <p className="quick-add-modal__place">
                {[
                  addressParts?.city,
                  addressParts?.state,
                  addressParts?.zip,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            </div>
            {/* Inviting the agent to type into fields that are about to be
                overwritten was the wrong offer. The fields read as pending
                until they are filled, and the copy just says what is happening. */}
            <p
              className="quick-add-modal__note"
              aria-live="polite"
              role="status"
            >
              {enriching ? (
                <>
                  <span
                    className="quick-add-modal__inline-spinner"
                    aria-hidden="true"
                  />
                  Filling this in from the MLS and public property records...
                </>
              ) : (
                SOURCE_LABELS[source] ||
                "We couldn't find records for this address. Fill in what you know."
              )}
            </p>

            <div
              className={`quick-add-modal__grid${
                enriching ? " quick-add-modal__grid--pending" : ""
              }`}
              aria-busy={enriching ? "true" : "false"}
            >
              <label>
                <span>Property type</span>
                <select
                  className={`form-select${pendingClass("prprtyType")}${
                    missingPropertyType && !enriching ? " is-required" : ""
                  }`}
                  value={values.prprtyType}
                  onChange={setValue("prprtyType")}
                  aria-invalid={missingPropertyType && !enriching}
                >
                  <option value="">Select</option>
                  {propertyTypes.map((pt) => (
                    <option key={pt.PropertyTypeId} value={pt.PropertyTypeId}>
                      {pt.PropertyTypeText}
                    </option>
                  ))}
                </select>
                {/* The MLS record carries no property type, so this is blank
                    more often than the other fields and the agent needs telling
                    which one is holding up the save. */}
                {missingPropertyType && !enriching && (
                  <small className="quick-add-modal__required">
                    Pick a type to add this property
                  </small>
                )}
              </label>
              <label>
                <span>Estimated price</span>
                {/* Formatted as it is typed rather than on blur: a price only
                    reads as a price with its separators, and swapping to raw
                    digits on focus meant it never looked like one while the
                    agent was actually looking at it. Digits are what get
                    stored, so the display never has to be parsed back. */}
                <input
                  className={`form-control${pendingClass("sellAskngPrice")}`}
                  inputMode="numeric"
                  value={formatMoney(values.sellAskngPrice)}
                  onChange={(e) => {
                    editedRef.current.add("sellAskngPrice");
                    setValues((prev) => ({
                      ...prev,
                      sellAskngPrice: e.target.value.replace(/[^\d]/g, ""),
                    }));
                  }}
                  placeholder="$0"
                />
              </label>
              <label>
                <span>Beds</span>
                <input
                  className={`form-control${pendingClass("noBedRm")}`}
                  inputMode="numeric"
                  value={values.noBedRm}
                  onChange={setValue("noBedRm")}
                />
              </label>
              <label>
                <span>Baths</span>
                <input
                  className={`form-control${pendingClass("noBathRm")}`}
                  inputMode="decimal"
                  value={values.noBathRm}
                  onChange={setValue("noBathRm")}
                />
              </label>
              <label>
                <span>Square feet</span>
                <input
                  className={`form-control${pendingClass("sqFt")}`}
                  inputMode="numeric"
                  value={values.sqFt}
                  onChange={setValue("sqFt")}
                />
              </label>
              <label>
                <span>Lot size (acres)</span>
                <input
                  className={`form-control${pendingClass("lotSize")}`}
                  inputMode="decimal"
                  value={values.lotSize}
                  onChange={setValue("lotSize")}
                />
              </label>
              <label>
                <span>Garage spots</span>
                <input
                  className={`form-control${pendingClass("grgeSpots")}`}
                  inputMode="numeric"
                  value={values.grgeSpots}
                  onChange={setValue("grgeSpots")}
                />
              </label>
              <label>
                <span>Stories</span>
                <input
                  className={`form-control${pendingClass("stories")}`}
                  inputMode="numeric"
                  value={values.stories}
                  onChange={setValue("stories")}
                />
              </label>
              <label>
                <span>Year built</span>
                <input
                  className={`form-control${pendingClass("yearBuilt")}`}
                  inputMode="numeric"
                  value={values.yearBuilt}
                  onChange={setValue("yearBuilt")}
                />
              </label>
            </div>

            <div className="quick-add-modal__features">
              <span className="quick-add-modal__features-label">
                Property features
                {niceToHaves.length > 0 ? ` (${niceToHaves.length})` : ""}
              </span>

              {niceToHaves.length > 0 ? (
                <div className="quick-add-modal__chips">
                  {niceToHaves.map((f) => (
                    <span
                      key={`${f.tab}-${f.id}`}
                      className="quick-add-modal__chip"
                    >
                      {f.label}
                      <button
                        type="button"
                        aria-label={`Remove ${f.label}`}
                        onClick={() =>
                          setNiceToHaves((prev) =>
                            prev.filter(
                              (x) => !(x.tab === f.tab && x.id === f.id),
                            ),
                          )
                        }
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="quick-add-modal__features-note">
                  {enriching
                    ? "Looking for features on this property..."
                    : "None detected. Add any that apply."}
                </p>
              )}

              {/* A native select cannot be typed into and draws its own arrow,
                  so the list had to be scrolled to find anything in it. The
                  same option ids repeat across tabs, hence the composite
                  option value. Menu is portalled so the modal cannot clip it. */}
              <Select
                className="quick-add-modal__add-feature"
                classNamePrefix="select"
                placeholder="+ Add a feature"
                options={featureCatalogue.filter(
                  (o) => !niceToHaves.some((f) => f.tab === o.tab && f.id === o.id),
                )}
                getOptionValue={(o) => `${o.tab}:${o.id}`}
                onChange={(picked) => {
                  if (picked) setNiceToHaves((prev) => [...prev, picked]);
                }}
                value={null}
                isSearchable
                noOptionsMessage={() => "No features match that"}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                // Portalled to escape the modal's clipping, but a portalled menu
                // is absolutely positioned on <body>, so opening one near the
                // bottom grew the page and scrolled it. Fixed positioning takes
                // it out of that flow, and auto placement flips it above the
                // control when the room below is not there.
                menuPosition="fixed"
                menuPlacement="auto"
                maxMenuHeight={220}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 1100 }),
                }}
              />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="quick-add-modal__cancel"
          onClick={onHide}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="quick-add-modal__confirm"
          onClick={handleConfirm}
          disabled={status !== "ready" || saving || missingPropertyType}
        >
          {saving ? "Adding..." : "Add property"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuickAddPropertyModal;
